// "Set a password" nudge for accounts that arrived through a magic-login
// link (auto-provisioned from email + name, no password yet). Mounted once
// in App.jsx: about 60s after sign-in on ANY page (issue, schedule,
// dashboard) a small card slides in top-right - big X or "Not now"
// dismisses it for the session; saving calls POST /users/set-password so
// the normal login works from then on. Nothing blocks the page.
import React, { useEffect, useState } from "react";
import { Button, Form, Input, Modal, message } from "antd";
import { url, tokenRef } from "api";

export const NEEDS_PASSWORD_KEY = "bl_magic_needs_password";
const DELAY_MS = 30 * 1000;

export default function SetPasswordNudge({ userInfo }) {
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form] = Form.useForm();
  const uid = userInfo?.id || userInfo?._id;
  // Only for the partner account that arrived by magic link (flag holds
  // its user id): never for licence holders / admins / other roles.
  const eligible = !!uid && userInfo?.role === "Responsible" && !userInfo?.admin && !userInfo?.companyAdmin
    && sessionStorage.getItem(NEEDS_PASSWORD_KEY) === String(uid);
  useEffect(() => {
    if (!eligible) return undefined;
    const t = setTimeout(() => {
      if (sessionStorage.getItem(NEEDS_PASSWORD_KEY) === String(uid)) setOpen(true);
    }, DELAY_MS);
    return () => clearTimeout(t);
  }, [eligible, uid]);
  const dismiss = () => { sessionStorage.removeItem(NEEDS_PASSWORD_KEY); setOpen(false); };
  const submit = async () => {
    let values;
    try { values = await form.validateFields(); } catch { return; }
    setSaving(true);
    try {
      const resp = await fetch(`${url}/users/set-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${localStorage.getItem(tokenRef)}` },
        body: JSON.stringify({ password: values.password, phone: values.phone || "" }),
      });
      const body = await resp.json().catch(() => ({}));
      if (!resp.ok) throw new Error(body.err || "Could not set password");
      message.success("Saved. You can now also log in with your email and password.");
      dismiss();
    } catch (e) {
      message.error(e.message || "Could not set password");
    } finally {
      setSaving(false);
    }
  };
  if (!eligible) return null;
  return (
    <Modal
      open={open}
      onCancel={dismiss}
      footer={null}
      width={360}
      centered={false}
      mask={false}
      maskClosable
      style={{ top: 72, right: 24, position: "absolute", margin: 0 }}
      closeIcon={<span style={{ fontSize: 26, lineHeight: "26px", fontWeight: 700, color: "#111827" }}>×</span>}
      bodyStyle={{ padding: "18px 20px 14px" }}
      zIndex={2000}
    >
      <div style={{ fontWeight: 700, color: "#111827", marginBottom: 4, paddingRight: 24 }}>Want to come back later?</div>
      <div style={{ fontSize: 12.5, color: "#6b7280", marginBottom: 12 }}>
        You are signed in as <b>{[userInfo?.name, userInfo?.lastName].filter(Boolean).join(" ") || userInfo?.email}</b>
        {userInfo?.companyName ? <> at <b>{userInfo.companyName}</b></> : null} ({userInfo?.email}). Set a password to log in any time, and a phone number so the team can reach you. Both optional.
      </div>
      <Form form={form} layout="vertical" size="small" onFinish={submit}>
        <Form.Item name="password" style={{ marginBottom: 8 }}
          rules={[{ required: true, message: "8+ characters" }, { min: 8, message: "8+ characters" }]}>
          <Input.Password placeholder="New password" autoComplete="new-password" />
        </Form.Item>
        <Form.Item name="confirm" dependencies={["password"]} style={{ marginBottom: 8 }}
          rules={[{ required: true, message: "Repeat it" }, ({ getFieldValue }) => ({
            validator: (_, v) => (!v || v === getFieldValue("password") ? Promise.resolve() : Promise.reject(new Error("Passwords do not match"))),
          })]}>
          <Input.Password placeholder="Repeat password" autoComplete="new-password" />
        </Form.Item>
        <Form.Item name="phone" style={{ marginBottom: 10 }}>
          <Input placeholder="Phone (optional)" autoComplete="tel" />
        </Form.Item>
        <div style={{ display: "flex", gap: 8 }}>
          <Button size="small" onClick={dismiss}>Not now</Button>
          <Button size="small" type="primary" htmlType="submit" loading={saving} style={{ flex: 1 }}>Save password</Button>
        </div>
      </Form>
    </Modal>
  );
}
